/**
 * Voucher Composer V2 · Preset library.
 *
 * A preset is a full canonical V2 design (canvas + elements) that
 * seeds the editor. Every element remains freely editable after the
 * preset is applied. Coordinates and sizes are in NORMALISED units
 * (0.0 – 1.0 of the canvas). Font sizes are expressed as fractions of
 * canvas WIDTH so the same design scales identically at any size and
 * on any device.
 *
 * Aspect ratio is fixed to 1.586 (standard credit-card ratio) so the
 * voucher reads as a premium privilege card on both desktop and phone.
 *
 * IMPORTANT — DO NOT change the `id` values of the six functional
 * elements: brand, title, value, qr, validity, terms_cta. The
 * renderer relies on these ids to bind live voucher data.
 */

import { PALETTES } from './voucherDesign';

const A = 1.586;   // legacy canvas aspect ratio (w / h)

// Phase 2A · Premium Wide is the DEFAULT for new/blank canvases.
// Existing legacy vouchers keep whatever ratio they were saved with.
export const ASPECT_RATIOS = [
  { key: 'premium_wide',      label: 'Premium Wide',      value: 1.9 },
  { key: 'panoramic',         label: 'Panoramic',         value: 2.2 },
  { key: 'classic_landscape', label: 'Classic Landscape', value: 1.586 },
  { key: 'square',            label: 'Square',            value: 1.0 },
  { key: 'portrait',          label: 'Portrait',          value: 0.72 },
];
export const DEFAULT_ASPECT_RATIO = 1.9;

// A stripped canvas with no seed elements — literally "Start Blank".
export const BLANK_PRESET = {
  key: 'blank',
  label: 'Blank Canvas',
  thumbnail_bg: 'linear-gradient(135deg, #E5E7EB 0%, #CBD5E1 100%)',
  build: () => ({
    design_version: 2,
    canvas: {
      aspect_ratio: DEFAULT_ASPECT_RATIO,
      background: { type: 'solid', value: '#0E2C22' },
      border: { color: 'rgba(255,255,255,0.10)', width: 1, radius: 20 },
      shadow: '0 22px 60px -18px rgba(0,0,0,0.55)',
    },
    elements: [],   // ← Phase 2A: no forced content layers
  }),
};

// ── Element factories ──────────────────────────────────────────
const brandEl = ({ x = 0.055, y = 0.08, color = '#F7F5EE', size = 0.038 } = {}) => ({
  id: 'brand', type: 'brand',
  x, y, width: 0.55, height: 0.09, rotation: 0, z_index: 10, opacity: 1, locked: false,
  style: { color, font: 'Montserrat', weight: 700, size, letter_spacing: '0.06em', align: 'left' },
  content: { show_tagline: true },
});

const logoEl = ({ x = 0.79, y = 0.06, size = 0.14 } = {}) => ({
  id: 'logo', type: 'logo',
  x, y, width: size, height: size * A, rotation: 0, z_index: 11, opacity: 1, locked: false,
  style: { fit: 'contain', border_radius: 999, background: 'transparent' },
  content: {},
});

const titleEl = ({ y = 0.34, color = '#F7F5EE', size = 0.052 } = {}) => ({
  id: 'title', type: 'title',
  x: 0.08, y, width: 0.84, height: 0.09, rotation: 0, z_index: 12, opacity: 1, locked: false,
  style: { color, font: 'Playfair Display', weight: 700, size, align: 'center', letter_spacing: '0.02em' },
  content: {},
});

const subtitleEl = ({ y = 0.44, color = 'rgba(247,245,238,0.75)', size = 0.028 } = {}) => ({
  id: 'subtitle', type: 'subtitle',
  x: 0.08, y, width: 0.84, height: 0.06, rotation: 0, z_index: 13, opacity: 1, locked: false,
  style: { color, font: 'Inter', weight: 500, size, align: 'center', letter_spacing: '0.15em', text_transform: 'uppercase' },
  content: {},
});

const valueEl = ({ y = 0.52, color = '#C9A227', size = 0.11 } = {}) => ({
  id: 'value', type: 'value',
  x: 0.08, y, width: 0.84, height: 0.16, rotation: 0, z_index: 14, opacity: 1, locked: false,
  style: { color, font: 'Playfair Display', weight: 800, size, align: 'center' },
  content: {},
});

const qrEl = ({ x = 0.08, y = 0.72, size = 0.20 } = {}) => ({
  id: 'qr', type: 'qr',
  x, y, width: size, height: size * A, rotation: 0, z_index: 15, opacity: 1, locked: false,
  style: { fg: '#0E2C22', bg: '#F7F5EE', border_radius: 12, padding: 0.012 },
  content: {},
});

const codeEl = ({ y = 0.76, color = '#F7F5EE' } = {}) => ({
  id: 'code', type: 'code',
  x: 0.34, y, width: 0.58, height: 0.06, rotation: 0, z_index: 16, opacity: 1, locked: false,
  style: { color, font: 'JetBrains Mono, monospace', weight: 600, size: 0.032, align: 'left', letter_spacing: '0.14em' },
  content: {},
});

const validityEl = ({ y = 0.85, color = 'rgba(247,245,238,0.72)' } = {}) => ({
  id: 'validity', type: 'validity',
  x: 0.34, y, width: 0.58, height: 0.05, rotation: 0, z_index: 17, opacity: 1, locked: false,
  style: { color, font: 'Inter', weight: 500, size: 0.024, align: 'left', letter_spacing: '0.08em' },
  content: {},
});

const termsEl = ({ y = 0.91, color = 'rgba(247,245,238,0.55)' } = {}) => ({
  id: 'terms_cta', type: 'terms_cta',
  x: 0.34, y, width: 0.58, height: 0.045, rotation: 0, z_index: 18, opacity: 1, locked: false,
  style: { color, font: 'Inter', weight: 400, size: 0.020, align: 'left', text_decoration: 'underline' },
  content: {},
});

const uuid = () => (Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 12);

// Convenience for decorative helpers used by presets.
const divider = (opts) => ({
  id: `divider_${uuid()}`, type: 'line', x: 0.08, y: 0.29, width: 0.84, height: 0.002,
  rotation: 0, z_index: 5, opacity: 0.9, locked: false,
  style: { color: '#C9A227', thickness: 1.4, dash: 'solid' }, content: {}, ...opts,
});
const chipEl = (label, opts = {}) => ({
  id: `chip_${uuid()}`, type: 'chip', x: 0.68, y: 0.055, width: 0.24, height: 0.048,
  rotation: 0, z_index: 20, opacity: 1, locked: false,
  style: { color: '#0E2C22', bg: '#C9A227', font: 'Inter', weight: 700, size: 0.020,
           align: 'center', letter_spacing: '0.20em', text_transform: 'uppercase', border_radius: 999,
           padding_x: 0.02, padding_y: 0.008 },
  content: { text: label }, ...opts,
});
const rect = (opts = {}) => ({
  id: `rect_${uuid()}`, type: 'rectangle',
  x: 0.5, y: 0.5, width: 0.2, height: 0.06,
  rotation: 0, z_index: 4, opacity: 1, locked: false,
  style: { fill: '#C9A227', border_radius: 8, stroke: 'transparent', stroke_width: 0 },
  content: {}, ...opts,
});

// ── Presets ────────────────────────────────────────────────────
export const COMPOSER_PRESETS = [
  {
    key: 'royal_gold',
    label: 'Royal Gold',
    thumbnail_bg: PALETTES.dark_green_gold.background,
    build: () => ({
      design_version: 2,
      canvas: {
        aspect_ratio: A,
        background: { type: 'gradient', value: PALETTES.dark_green_gold.background },
        border: { color: 'rgba(201,162,39,0.32)', width: 1, radius: 24 },
        shadow: '0 22px 60px -18px rgba(0,0,0,0.55)',
      },
      elements: [
        divider({ y: 0.29, style: { color: '#C9A227', thickness: 1.6, dash: 'solid' } }),
        divider({ y: 0.71, style: { color: '#C9A227', thickness: 1.2, dash: 'solid' } }),
        chipEl('Premium', { x: 0.66, y: 0.055, style: { color: '#0E2C22', bg: '#C9A227', font: 'Inter', weight: 700, size: 0.020, align: 'center', letter_spacing: '0.20em', text_transform: 'uppercase', border_radius: 999, padding_x: 0.02, padding_y: 0.008 } }),
        brandEl(),
        logoEl(),
        titleEl(),
        subtitleEl(),
        valueEl(),
        qrEl(),
        codeEl(),
        validityEl(),
        termsEl(),
      ],
    }),
  },
  {
    key: 'emerald_club',
    label: 'Emerald Club',
    thumbnail_bg: 'linear-gradient(135deg, #1B5E4B 0%, #0F3B2E 100%)',
    build: () => ({
      design_version: 2,
      canvas: {
        aspect_ratio: A,
        background: { type: 'gradient', value: 'linear-gradient(135deg, #1B5E4B 0%, #0F3B2E 100%)' },
        border: { color: 'rgba(215,180,71,0.30)', width: 1, radius: 20 },
        shadow: '0 22px 60px -18px rgba(0,0,0,0.55)',
      },
      elements: [
        divider({ y: 0.30, style: { color: '#D7B447', thickness: 1.2, dash: 'solid' } }),
        chipEl('Club', { x: 0.67, y: 0.055, style: { color: '#0F3B2E', bg: '#D7B447', font: 'Inter', weight: 700, size: 0.020, align: 'center', letter_spacing: '0.20em', text_transform: 'uppercase', border_radius: 999, padding_x: 0.02, padding_y: 0.008 } }),
        brandEl({ color: '#F5F1E4' }),
        logoEl(),
        titleEl({ color: '#F5F1E4' }),
        subtitleEl({ color: 'rgba(245,241,228,0.75)' }),
        valueEl({ color: '#D7B447' }),
        qrEl({ x: 0.08, y: 0.72, size: 0.20 }),
        codeEl({ color: '#F5F1E4' }),
        validityEl({ color: 'rgba(245,241,228,0.72)' }),
        termsEl({ color: 'rgba(245,241,228,0.55)' }),
      ],
    }),
  },
  {
    key: 'midnight_premium',
    label: 'Midnight Premium',
    thumbnail_bg: 'linear-gradient(135deg, #1F1F23 0%, #101013 100%)',
    build: () => ({
      design_version: 2,
      canvas: {
        aspect_ratio: A,
        background: { type: 'gradient', value: 'linear-gradient(135deg, #1F1F23 0%, #101013 100%)' },
        border: { color: 'rgba(212,175,55,0.32)', width: 1, radius: 20 },
        shadow: '0 22px 60px -18px rgba(0,0,0,0.65)',
      },
      elements: [
        rect({ x: 0.0, y: 0.0, width: 1.0, height: 0.006, style: { fill: '#D4AF37', border_radius: 0 } }),
        rect({ x: 0.0, y: 0.994, width: 1.0, height: 0.006, style: { fill: '#D4AF37', border_radius: 0 } }),
        chipEl('VIP', { x: 0.70, y: 0.055, style: { color: '#101013', bg: '#D4AF37', font: 'Inter', weight: 800, size: 0.020, align: 'center', letter_spacing: '0.22em', text_transform: 'uppercase', border_radius: 999, padding_x: 0.02, padding_y: 0.008 } }),
        brandEl({ color: '#EFEEE9' }),
        logoEl(),
        titleEl({ color: '#EFEEE9' }),
        subtitleEl({ color: 'rgba(239,238,233,0.75)' }),
        valueEl({ color: '#D4AF37' }),
        qrEl(),
        codeEl({ color: '#EFEEE9' }),
        validityEl({ color: 'rgba(239,238,233,0.68)' }),
        termsEl({ color: 'rgba(239,238,233,0.5)' }),
      ],
    }),
  },
  {
    key: 'celebration',
    label: 'Celebration',
    thumbnail_bg: 'linear-gradient(135deg, #4A1220 0%, #2D0A14 100%)',
    build: () => ({
      design_version: 2,
      canvas: {
        aspect_ratio: A,
        background: { type: 'gradient', value: 'linear-gradient(135deg, #4A1220 0%, #2D0A14 100%)' },
        border: { color: 'rgba(228,185,66,0.32)', width: 1, radius: 22 },
        shadow: '0 22px 60px -18px rgba(0,0,0,0.65)',
      },
      elements: [
        divider({ y: 0.29, style: { color: '#E4B942', thickness: 1.4, dash: 'solid' } }),
        chipEl('Special', { x: 0.65, y: 0.055, style: { color: '#4A1220', bg: '#E4B942', font: 'Inter', weight: 700, size: 0.020, align: 'center', letter_spacing: '0.20em', text_transform: 'uppercase', border_radius: 999, padding_x: 0.02, padding_y: 0.008 } }),
        brandEl({ color: '#F7EDDD' }),
        logoEl(),
        titleEl({ color: '#F7EDDD' }),
        subtitleEl({ color: 'rgba(247,237,221,0.75)' }),
        valueEl({ color: '#E4B942' }),
        qrEl(),
        codeEl({ color: '#F7EDDD' }),
        validityEl({ color: 'rgba(247,237,221,0.72)' }),
        termsEl({ color: 'rgba(247,237,221,0.55)' }),
      ],
    }),
  },
  {
    key: 'minimal_ivory',
    label: 'Minimal Ivory',
    thumbnail_bg: 'linear-gradient(135deg, #FBF8F1 0%, #F1EBDC 100%)',
    build: () => ({
      design_version: 2,
      canvas: {
        aspect_ratio: A,
        background: { type: 'gradient', value: 'linear-gradient(135deg, #FBF8F1 0%, #F1EBDC 100%)' },
        border: { color: 'rgba(20,62,48,0.28)', width: 1, radius: 20 },
        shadow: '0 18px 50px -20px rgba(20,62,48,0.35)',
      },
      elements: [
        divider({ y: 0.29, style: { color: '#143E30', thickness: 1, dash: 'solid' } }),
        brandEl({ color: '#143E30' }),
        logoEl(),
        titleEl({ color: '#143E30' }),
        subtitleEl({ color: 'rgba(20,62,48,0.7)' }),
        valueEl({ color: '#143E30' }),
        qrEl({ style: { fg: '#143E30', bg: '#FBF8F1', border_radius: 12, padding: 0.012 } }),
        codeEl({ color: '#143E30' }),
        validityEl({ color: 'rgba(20,62,48,0.72)' }),
        termsEl({ color: 'rgba(20,62,48,0.55)' }),
      ],
    }),
  },
  {
    key: 'festive',
    label: 'Festive',
    thumbnail_bg: 'linear-gradient(135deg, #0E2543 0%, #071634 100%)',
    build: () => ({
      design_version: 2,
      canvas: {
        aspect_ratio: A,
        background: { type: 'gradient', value: 'linear-gradient(135deg, #0E2543 0%, #071634 100%)' },
        border: { color: 'rgba(212,175,55,0.38)', width: 1, radius: 22 },
        shadow: '0 22px 60px -18px rgba(0,0,0,0.60)',
      },
      elements: [
        rect({ x: 0.03, y: 0.03, width: 0.94, height: 0.005, style: { fill: '#D4AF37', border_radius: 4 } }),
        rect({ x: 0.03, y: 0.965, width: 0.94, height: 0.005, style: { fill: '#D4AF37', border_radius: 4 } }),
        chipEl('Signature', { x: 0.62, y: 0.055, style: { color: '#0E2543', bg: '#D4AF37', font: 'Inter', weight: 700, size: 0.020, align: 'center', letter_spacing: '0.20em', text_transform: 'uppercase', border_radius: 999, padding_x: 0.02, padding_y: 0.008 } }),
        brandEl({ color: '#F5F1E4' }),
        logoEl(),
        titleEl({ color: '#F5F1E4' }),
        subtitleEl({ color: 'rgba(245,241,228,0.75)' }),
        valueEl({ color: '#D4AF37' }),
        qrEl(),
        codeEl({ color: '#F5F1E4' }),
        validityEl({ color: 'rgba(245,241,228,0.72)' }),
        termsEl({ color: 'rgba(245,241,228,0.55)' }),
      ],
    }),
  },
];

// ── Migration: convert a legacy V1 design → V2 canonical composition ──
// Preserves the visual essence so an admin who clicks "Edit in Visual
// Designer" for an old voucher doesn't lose it.
export function migrateLegacyToV2(legacyDesign = {}, paletteKey = 'dark_green_gold') {
  const palette = PALETTES[paletteKey] || PALETTES.dark_green_gold;
  const text = palette.text;
  const accent = legacyDesign.accent_color || palette.accent;
  const bg = legacyDesign.background_image
    ? `url(${legacyDesign.background_image}) center/cover`
    : palette.background;
  const chip = legacyDesign.chip_label && legacyDesign.chip_label !== 'none' ? legacyDesign.chip_label : null;
  const els = [
    divider({ y: 0.29, style: { color: accent, thickness: 1.4, dash: 'solid' } }),
    brandEl({ color: text }),
    logoEl(),
    titleEl({ color: text }),
    subtitleEl({ color: text, size: 0.028 }),
    valueEl({ color: accent }),
    qrEl({ style: { fg: palette.qrFg, bg: palette.qrBg, border_radius: 12, padding: 0.012 } }),
    codeEl({ color: text }),
    validityEl({ color: text }),
    termsEl({ color: text }),
  ];
  if (chip) {
    els.unshift(chipEl(chip, { x: 0.66, y: 0.055,
      style: { color: palette.surface, bg: accent, font: 'Inter', weight: 700, size: 0.020,
               align: 'center', letter_spacing: '0.20em', text_transform: 'uppercase',
               border_radius: 999, padding_x: 0.02, padding_y: 0.008 } }));
  }
  return {
    design_version: 2,
    canvas: {
      aspect_ratio: A,
      background: { type: 'gradient', value: bg },
      border: { color: `${accent}55`, width: 1, radius: 22 },
      shadow: '0 22px 60px -18px rgba(0,0,0,0.55)',
    },
    elements: els,
    // keep the legacy fields intact so old renderers can still read the
    // document if the version flag is ever downgraded.
    _legacy: {
      theme: legacyDesign.theme,
      accent_color: legacyDesign.accent_color,
      chip_label: legacyDesign.chip_label,
      corner_style: legacyDesign.corner_style,
      shimmer: legacyDesign.shimmer,
    },
  };
}

export const A_RATIO = A;
export const newElementId = (type) => `${type}_${uuid()}`;
